import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import AuthContext from '../context/AuthContext';
import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { ShellContext } from './layout/AppShell';
import { SectionCard } from './ui';
import BusinessFeatures from './BusinessFeatures';
import CardPayments from './CardPayments';
import PaymentProviders from './PaymentProviders';
import { TRUE_COLORS } from '../theme';

const SettingRow = ({ icon, title, description, action }: { icon: React.ReactNode; title: string; description: string; action: React.ReactNode }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ py: 2, alignItems: { sm: 'center' }, borderTop: `1px solid ${TRUE_COLORS.borderSolid}`, '&:first-of-type': { borderTop: 'none', pt: 0 } }}>
    <Box sx={{ width: 40, height: 40, borderRadius: '10px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(20,37,44,0.06)', color: TRUE_COLORS.primary, flexShrink: 0 }}>
      {icon}
    </Box>
    <Box sx={{ flexGrow: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{description}</Typography>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{action}</Box>
  </Stack>
);

const Settings = () => {
  const navigate = useNavigate();
  const axios = useAxios();
  const { logoutUser, user } = React.useContext(AuthContext);
  const { organizationName, membership } = React.useContext(ShellContext);
  const [subscriptionError, setSubscriptionError] = React.useState(false);

  const openSubscription = async () => {
    try {
      const { data } = await axios.post('management/create-portal-session/');
      if (data?.url) { window.location.href = data.url; return; }
    } catch {
      // Falls through to the notice below.
    }
    setSubscriptionError(true);
  };

  return (
    <PageShell title="Configuración" subtitle="Tu negocio y sus funciones, tu cuenta, tu suscripción y la conexión con TRUE POS">
      <Grid container spacing={3}>
        <Grid size={12}>
          <BusinessFeatures />
        </Grid>
        {membership?.is_admin && (
          <Grid size={12}>
            <Stack spacing={3}>
              <CardPayments />
              <PaymentProviders />
            </Stack>
          </Grid>
        )}
        <Grid size={{ xs: 12, lg: 7 }}>
          <SectionCard title="General">
            {subscriptionError && (
              <Alert severity="info" onClose={() => setSubscriptionError(false)} sx={{ mb: 2 }}>
                No tienes una suscripción activa.{' '}
                <a href="mailto:luis@truedata.com.mx,dipepere@truedata.com.mx?subject=Plan TRUE POS">Escríbenos</a> y te ayudamos a elegir tu plan.
              </Alert>
            )}
            <Box>
              <SettingRow
                icon={<PersonOutlineIcon />}
                title="Mi perfil"
                description="Nombre, teléfono y contraseña."
                action={<Button endIcon={<ChevronRightIcon />} onClick={() => navigate('/admin/profile')}>Abrir</Button>}
              />
              <SettingRow
                icon={<BusinessOutlinedIcon />}
                title="Organización"
                description={`${organizationName || 'Tu organización'}: sucursales, cajas y equipo.`}
                action={<Button endIcon={<ChevronRightIcon />} onClick={() => navigate('/admin/organization')}>Abrir</Button>}
              />
              <SettingRow
                icon={<CreditCardOutlinedIcon />}
                title="Suscripción"
                description="Administra tu plan, métodos de pago y facturas."
                action={<Button endIcon={<ChevronRightIcon />} onClick={openSubscription}>Administrar</Button>}
              />
              <SettingRow
                icon={<LogoutIcon />}
                title="Cerrar sesión"
                description={`Sesión iniciada como ${user?.username ?? ''}.`}
                action={<Button color="error" onClick={logoutUser}>Cerrar sesión</Button>}
              />
            </Box>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <SectionCard
            title="Conecta TRUE POS"
            subtitle="La app de escritorio para cobrar en caja"
            action={<Button size="small" variant="contained" onClick={() => navigate('/pos#descargar')}>Descargar</Button>}
          >
            <Stepper orientation="vertical" activeStep={-1} sx={{ '& .MuiStepLabel-label': { fontWeight: 600, fontSize: '0.875rem' } }}>
              {[
                { title: 'Instala TRUE POS', body: 'Descárgalo para Windows o macOS e instálalo en cada computadora que funcione como caja.' },
                { title: 'Inicia sesión', body: 'Usa tu correo y contraseña, o la cuenta de cajero que creaste en Organización.' },
                { title: 'Elige la sucursal', body: 'En TRUE POS ve a Configuración y selecciona la sucursal de esa caja.' },
                { title: 'Empieza a vender', body: 'Productos, precios y promociones se sincronizan solos; las ventas aparecen aquí en minutos.' },
              ].map(step => (
                <Step key={step.title} active expanded>
                  <StepLabel>{step.title}</StepLabel>
                  <StepContent>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>{step.body}</Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </SectionCard>
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default Settings;
